import { useState, useEffect } from "react";
import {
  Card,
  IndexTable,
  BlockStack,
  IndexFilters,
  Page,
  IndexFiltersMode,
  Box,
  Text,
  IndexFiltersProps,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { AppPageWrapper } from "@shared/ui";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { TabsTableSkeleton } from "@shared/ui";
import { RenderDate } from "@shared/ui";
import { formatStartDate } from "@shared/lib";
import { Event, EventStatusType } from "@features/events/types";
import EventBadgeRenderer from "@features/events/components/EventBadgeRenderer";
import IconWithValueAndTooltip from "@features/events/components/common/IconWithValueAndTooltip";
import { ViewIcon, TeamIcon, LinkIcon } from "@medentic/polaris-icons";
import { getLocalizedText } from "@shared/lib";
import InstructorsAvatarGroup from "@features/events/components/InstructorsAvatarGroup";

const STATUS_TABS: Array<{ id: "all" | EventStatusType }> = [
  { id: "all" },
  { id: EventStatusType.ONGOING },
  { id: EventStatusType.UPCOMING },
  { id: EventStatusType.COMPLETED },
];

const getTabTranslationKey = (tabId: string): string => {
  const tabKeyMap: Record<string, string> = {
    all: "eventsList.tab_all",
    [EventStatusType.ONGOING]: "eventsList.tab_ongoing",
    [EventStatusType.UPCOMING]: "eventsList.tab_upcoming",
    [EventStatusType.COMPLETED]: "eventsList.tab_completed",
  };
  return tabKeyMap[tabId] || `eventsList.tab_${tabId}`;
};

export default function EventsList() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { GET } = useApiRequest();
  const serializedSearchParams = searchParams.toString();

  // State
  const [selectedTab, setSelectedTab] = useState(
    () => searchParams.get("tab") || "all"
  );
  const [queryValue, setQueryValue] = useState(
    () => searchParams.get("search") || ""
  );
  const [debouncedQuery, setDebouncedQuery] = useState(queryValue);
  const [sortSelected, setSortSelected] = useState<string[]>(() => {
    const sortParam = searchParams.get("sort");
    if (sortParam) return [sortParam];
    
    // Check localStorage for saved sorting preference
    const savedSort = localStorage.getItem("events_list_sorting");
    return savedSort ? [savedSort] : ["starting desc"];
  });
  const [mode, setMode] = useState(IndexFiltersMode.Default);

  // Sort options
  const sortOptions: IndexFiltersProps['sortOptions'] = [
    {label: t('eventsList.sort_by.created'), value: 'created asc', directionLabel: t('common.oldestToNewest')},
    {label: t('eventsList.sort_by.created'), value: 'created desc', directionLabel: t('common.newestToOldest')},
    
    {label: t('eventsList.sort_by.modified'), value: 'updated asc', directionLabel: t('common.oldestToNewest')},
    {label: t('eventsList.sort_by.modified'), value: 'updated desc', directionLabel: t('common.newestToOldest')},
    
    {label: t('eventsList.sort_by.name'), value: 'name asc', directionLabel: t('common.AtoZ')},
    {label: t('eventsList.sort_by.name'), value: 'name desc', directionLabel: t('common.ZtoA')},
    
    {label: t('eventsList.sort_by.starting'), value: 'starting asc', directionLabel: t('common.oldestToNewest')},
    {label: t('eventsList.sort_by.starting'), value: 'starting desc', directionLabel: t('common.newestToOldest')},
    
    {label: t('eventsList.sort_by.ending'), value: 'ending asc', directionLabel: t('common.oldestToNewest')},
    {label: t('eventsList.sort_by.ending'), value: 'ending desc', directionLabel: t('common.newestToOldest')},
  ];

  // Save sorting preference to localStorage
  useEffect(() => {
    if (sortSelected[0]) {
      localStorage.setItem("events_list_sorting", sortSelected[0]);
    }
  }, [sortSelected]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(queryValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [queryValue]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedTab && selectedTab !== "all") params.set("tab", selectedTab);
    if (debouncedQuery) params.set("search", debouncedQuery);
    if (sortSelected[0]) params.set("sort", sortSelected[0]);
    setSearchParams(params);
  }, [selectedTab, debouncedQuery, sortSelected, setSearchParams]);

  // Fetch events
  const {
    data: events = [],
    isLoading,
    isFetching,
  } = useQuery<Event[]>({
    queryKey: ["events", hcc, selectedTab, debouncedQuery, sortSelected],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedTab && selectedTab !== "all") params.append("status", selectedTab);
      if (debouncedQuery) params.append("query", debouncedQuery);
      if (sortSelected[0]) params.append("sort", sortSelected[0]);
      return GET(`/api/healthcare-companies/${hcc}/e/events?${params}`);
    },
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });

  // Render event row elements
  const renderEventRow = (event: Event & { position?: number }) => (
    <IndexTable.Row
      id={event.id}
      key={event.id}
      position={event.position || 0}
      onClick={() =>
        navigate(
          `/e/${hcc}/events/${event.id}${
            serializedSearchParams ? `?${serializedSearchParams}` : ""
          }`
        )
      }
    >
      <IndexTable.Cell>
        <Box maxWidth="400px">
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Text as="span" variant="bodySm">
              {getLocalizedText(event.title)}
            </Text>
          </div>
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InstructorsAvatarGroup
          instructorIds={event.instructorIds}
          maxShow={5}
          size="small"
        />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <EventBadgeRenderer slug={event.visibility} entity="visibility" />
      </IndexTable.Cell>
      <IndexTable.Cell>
        <EventBadgeRenderer slug={event.status} entity="event" />
      </IndexTable.Cell>

      <IndexTable.Cell>
        {event.dateStart === event.dateEnd && event.dateStart ? (
          <RenderDate dayFirst={true} date={event.dateStart} />
        ) : (
          <>
            {formatStartDate(event.dateStart)}
            {event.dateEnd && (
              <>
                {" "}
                - <RenderDate dayFirst={true} date={event.dateEnd} />
              </>
            )}
          </>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box>
          <IconWithValueAndTooltip
            iconSource={ViewIcon}
            value={event.views ?? 0}
            tooltip={t("eventDashboard.views")}
            iconTone="subdued"
            textTone="subdued"
            alignment="end"
          />
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box>
          <IconWithValueAndTooltip
            iconSource={TeamIcon}
            value={event.spotsCurrent ?? 0}
            tooltip={t("eventDashboard.registered")}
            iconTone="subdued"
            textTone="subdued"
            alignment="end"
          />
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box>
          <IconWithValueAndTooltip
            iconSource={LinkIcon}
            value={event.bookRedirectLinkClicks ?? 0}
            tooltip={t("eventDashboard.redirectClicks")}
            iconTone="subdued"
            textTone="subdued"
            alignment="end"
          />
        </Box>
      </IndexTable.Cell>
    </IndexTable.Row>
  );

  // Table headings
  const headings: { title: string; alignment?: "start" | "end" }[] = [
    { title: t("eventsList.event") },
    { title: t("eventsList.instructors") },
    { title: t("eventsList.visibility") },
    { title: t("eventsList.status") },
    { title: t("eventsList.dates") },
    { title: t("eventsList.views"), alignment: "end" },
    { title: t("eventsList.registrations"), alignment: "end" },
    { title: t("eventsList.redirectClicks"), alignment: "end" },
  ];

  // Table rows
  const rowMarkup = events.map((event: Event, idx: number) => 
    renderEventRow({ ...event, position: idx })
  );

  const isInitialLoading = isLoading && events.length === 0;

  return (
    <AppPageWrapper>
      <Page
        fullWidth
        title={t("eventsList.title")}
        primaryAction={{
          content: t("eventsList.createEvent"),
          onAction: () => navigate(`/e/${hcc}/events/new`),
        }}
      >
        <BlockStack gap="200">
          <Card padding="0">
            <BlockStack gap="0">
              <IndexFilters
                queryValue={queryValue}
                queryPlaceholder={t("eventsList.searchPlaceholder")}
                onQueryChange={setQueryValue}
                onQueryClear={() => setQueryValue("")}
                onClearAll={() => {
                  setSelectedTab("all");
                  setQueryValue("");
                  setSortSelected(["start asc"]);
                }}
                sortOptions={sortOptions}
                sortSelected={sortSelected}
                onSort={setSortSelected}
                mode={mode}
                setMode={setMode}
                loading={isFetching && events.length > 0}
                filters={[]}
                tabs={STATUS_TABS.map((tab) => ({
                  id: tab.id,
                  content: t(getTabTranslationKey(tab.id)),
                  panelID: tab.id,
                }))}
                selected={STATUS_TABS.findIndex((t) => t.id === selectedTab)}
                onSelect={(selectedTabIndex) => {
                  setSelectedTab(STATUS_TABS[selectedTabIndex].id);
                }}
              />
              {isInitialLoading ? (
                <TabsTableSkeleton />
              ) : (
                <IndexTable
                  resourceName={{
                    singular: t("eventsList.event"),
                    plural: t("eventsList.events"),
                  }}
                  itemCount={events.length}
                  selectable={false}
                  headings={
                    headings as [{ title: string }, ...{ title: string }[]]
                  }
                >
                  {rowMarkup}
                </IndexTable>
              )}
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </AppPageWrapper>
  );
}
